import { Link } from 'react-router-dom';
import { waProductLink } from '@/config/site';
import type { ProdutoCuradoria } from '@/mocks/curadoria';

interface ProductShowcaseProps {
  produto: ProdutoCuradoria;
  /** Contexto usado no link de orçamento (mantém a origem do contato). */
  contexto?: string;
  /** Exibe a ressalva interna de validação comercial. */
  mostrarValidacao?: boolean;
}

const usoLabel: Record<string, string> = {
  intensivo: 'Uso intensivo',
  moderado: 'Uso moderado',
  residencial: 'Uso residencial',
};

export default function ProductShowcase({
  produto,
  contexto = 'curadoria',
  mostrarValidacao = false,
}: ProductShowcaseProps) {
  const identificacao = [produto.linha, produto.referencia].filter(Boolean).join(' · ');
  const temFotoReal =
    Boolean(produto.imagem) && !produto.imagem.includes('/api/search-image');

  return (
    <article className="group flex flex-col" data-product-shop>
      {temFotoReal ? (
        <div className="w-full aspect-[4/3] overflow-hidden rounded-[14px] bg-background-100">
          <img
            src={produto.imagem}
            alt={`${produto.nome} — ${produto.fabricante}`}
            title={`${produto.nome} ${produto.fabricante} — mobiliário corporativo Home Office Premium`}
            className="w-full h-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
          />
        </div>
      ) : (
        <div className="w-full aspect-[4/3] rounded-[14px] bg-background-50 border border-background-200 p-5 md:p-6 flex flex-col justify-between">
          <p className="text-[10px] uppercase tracking-[0.18em] text-foreground-400">
            {produto.fabricante}
          </p>
          <div>
            {produto.linha && (
              <p className="text-[12.5px] text-foreground-500">{produto.linha}</p>
            )}
            <p className="mt-1 text-[20px] md:text-[24px] leading-tight font-medium tracking-[-0.02em] text-foreground-950">
              {produto.referencia || produto.nome}
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 flex-1 flex flex-col">
        {temFotoReal && (
          <p className="text-[10px] uppercase tracking-[0.18em] text-foreground-400">
            {produto.fabricante}
          </p>
        )}

        <h3
          className={`${temFotoReal ? 'mt-2' : ''} text-[16px] md:text-[17px] leading-snug font-medium tracking-[-0.022em] text-foreground-950`}
        >
          {produto.nome}
        </h3>

        {temFotoReal && identificacao && (
          <p className="mt-1 text-[12px] tracking-[0.01em] text-foreground-400">{identificacao}</p>
        )}

        <p className="mt-2.5 text-[13px] leading-[1.6] text-foreground-500">{produto.aplicacao}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {produto.uso && (
            <span className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-1 text-[10.5px] font-medium tracking-[0.04em] text-secondary-900">
              {usoLabel[produto.uso]}
            </span>
          )}
          <span className="inline-flex items-center rounded-full bg-background-100 px-2.5 py-1 text-[10.5px] tracking-[0.02em] text-foreground-500">
            {produto.paginaPdf}
          </span>
        </div>

        {mostrarValidacao && produto.notaValidacao && (
          <p className="mt-2.5 flex items-start gap-1.5 text-[11px] leading-[1.5] text-foreground-400">
            <i className="ri-information-line mt-[1px] text-[12px] text-accent-500"></i>
            <span>{produto.notaValidacao}</span>
          </p>
        )}

        <div className="mt-4 pt-3 border-t border-background-200 flex flex-wrap items-center gap-x-5 gap-y-2">
          <a
            href={waProductLink(`${produto.nome} — ${produto.fabricante}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-foreground-950 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-[13px]"></i>
            Consultar disponibilidade
          </a>
          <Link
            to={`/orcamento?produto=${encodeURIComponent(produto.nome)}&origem=${contexto}`}
            className="inline-flex items-center gap-1 text-[12.5px] text-foreground-500 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            Solicitar orçamento
            <i className="ri-arrow-right-line text-[12px]"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}